/**
 * Where a building sprite actually meets the ground.
 *
 * Shadows were anchored to the bottom centre of the sprite's box, which is
 * not where these buildings stand. Measured across the art:
 *
 * - The base centre is off-centre and varies by race for the same building —
 *   `robotFactory` sits at 0.625 of the width for humans but 0.380 for axons,
 *   a quarter of the sprite apart. One hand-tuned offset per building type
 *   cannot be right for all three races.
 * - Several sprites have empty space below the building. `robots/robotFactory`
 *   bottoms out at 0.865 of the image height, so 13.5% of the sprite is air
 *   underneath — anchoring at the box bottom drew its shadow ~34px below where
 *   the building stands, which reads as hovering.
 *
 * So the base is measured from the sprite's own alpha instead of tuned by
 * hand: it is correct per race and per level variant, and stays correct when
 * the art is replaced.
 */
export interface SpriteBase {
    /** Base centre across the image, 0..1 from the left edge. */
    centerX: number;
    /** Lowest opaque row, 0..1 down from the top. 1 means the sprite touches the bottom edge. */
    bottomY: number;
    /** Width of the contact footprint, as a fraction of the image width. */
    width: number;
}

/** Alpha at or above this counts as solid sprite rather than a soft edge. */
const OPAQUE_ALPHA = 200;

/**
 * How much of the sprite, upward from its lowest opaque row, counts as the
 * part resting on the ground. Ten percent is enough to span a base that
 * recedes in the isometric projection without swallowing the body above it.
 */
const BASE_BAND_FRACTION = 0.1;

/**
 * Derives the contact base from RGBA pixels. Pure, so it is unit-testable
 * against known pixel grids — the canvas plumbing lives in `measureSpriteBase`.
 *
 * Returns `null` for a fully transparent image, where there is no base to find.
 */
export function computeBaseFromAlpha(data: Uint8ClampedArray, width: number, height: number): SpriteBase | null {
    let bottom = -1;
    let top = -1;
    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            if ((data[(y * width + x) * 4 + 3] ?? 0) >= OPAQUE_ALPHA) {
                if (top < 0) top = y;
                bottom = y;
                break;
            }
        }
    }
    if (bottom < 0) return null;

    const bandTop = Math.max(top, bottom - Math.floor(height * BASE_BAND_FRACTION));
    let lo = width;
    let hi = -1;
    for (let y = bandTop; y <= bottom; y += 1) {
        for (let x = 0; x < width; x += 1) {
            if ((data[(y * width + x) * 4 + 3] ?? 0) >= OPAQUE_ALPHA) {
                if (x < lo) lo = x;
                if (x > hi) hi = x;
            }
        }
    }
    if (hi < 0) return null;

    return {
        centerX: (lo + hi) / 2 / width,
        bottomY: (bottom + 1) / height,
        width: (hi - lo + 1) / width,
    };
}

const cache = new Map<string, SpriteBase | null>();

/**
 * Measures a loaded sprite's base, caching per image URL so each sprite is
 * read back from a canvas at most once for the life of the page.
 *
 * Returns `null` where the measurement cannot be taken — no 2D context, or a
 * canvas readback that throws — so the caller falls back to the sprite box.
 */
export function measureSpriteBase(image: HTMLImageElement): SpriteBase | null {
    const key = image.currentSrc || image.src;
    const cached = cache.get(key);
    if (cached !== undefined) return cached;

    let measured: SpriteBase | null = null;
    try {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (context && canvas.width > 0 && canvas.height > 0) {
            context.drawImage(image, 0, 0);
            const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
            measured = computeBaseFromAlpha(data, canvas.width, canvas.height);
        }
    } catch {
        measured = null;
    }

    cache.set(key, measured);
    return measured;
}

/** Test seam: drops the memoised measurements. */
export function clearSpriteBaseCache(): void {
    cache.clear();
}
